import React, { useState, useRef } from 'react'
import {
  Button,
  TextField,
  Divider,
  Heading,
  Typography,
  Frame,
  IconButton,
  AddPhotoIcon,
} from 'components'
import { Avatar, Switch as MuiSwitch, FormControlLabel, withStyles } from '@material-ui/core'
import { createUser, getFormValues, formatPhoneNumber, useContent } from 'utils'
import { Box } from '@material-ui/core'
import firebase from 'utils/firebase'
import { SUBSCRIPTIONS_ENABLED, CUSTOM_STATIC_LANDER, ONBOARD_WALKTHROUGH_URL, PROFILE_PICTURES_ENABLED, TUTORIAL_ENABLED, PRONOUNS_ENABLED, ONBOARD_PROFILE_PICTURE_STEP_ENABLED, ONBOARD_SKIP_VALIDATION, AGE_GATE_ENABLED } from 'constants'

const FreelancerCreateForm = () => {
  const [phoneNumber, setPhoneNumber] = useState('')
  const [avatarUrl, setAvatarUrl] = useState('')
  const [stepIndex, setStepIndex] = useState(0)
  const [displayPronouns, setDisplayPronouns] = useState(true)
  const [isOver18, setIsOver18] = useState(true)
  const videoRef = useRef(null)
  const { onboarding, landing } = useContent();
  const buttonColor = landing?.['onboarding-button-color'] || 'primary'
  const buttonSecondaryColor = landing?.['onboarding-button-secondary'] || '#6c757d'

  // Create custom styled Switch using colors from useContent hook
  const CustomSwitch = withStyles({
    switchBase: {
      color: buttonSecondaryColor,
      '&$checked': {
        color: buttonColor,
      },
      '&$checked + $track': {
        backgroundColor: buttonColor,
        opacity: 0.5,
      },
    },
    checked: {},
    track: {
      backgroundColor: buttonSecondaryColor,
      opacity: 0.3,
    },
  })(MuiSwitch);

  if (!onboarding) {
    return null
  }

  if (stepIndex === 0) {
    return (
      <Frame maxWidth="xs">
        <Heading>
          {onboarding['welcome-heading'] ? onboarding['welcome-heading'] : 'Employment status check'}
        </Heading>

        <Typography style={{ textAlign: 'center' }}>
          {onboarding['welcome-subheadings'] ? (
            onboarding['welcome-subheadings'].map(subheading => (
              <div style={{ margin: 4 }}>
                <>
                  {subheading}
                </>
                <br />
              </div>
            ))
          ) : (
            <>
              This Job Portal is for people who are actively looking for work and regularly taking on new projects.
              <br />
              {!CUSTOM_STATIC_LANDER && (
                <>
                  <>
                    This Job Portal is for people who are actively looking for work and regularly taking on new projects.
                  </>
                </>
              )}
              <br />
              <>
                Keep this in mind before you build a profile.
              </>
              <br />
              <>Thanks!</>
            </>
          )
          }
        </Typography>

        <Divider />

        <Box mx="auto" my={3} maxWidth={280}>
          <Button variant="contained" type="submit" style={{ width: '100%', 'background-color': buttonColor }} onClick={() => setStepIndex(1)}>
            Begin
          </Button>
        </Box>
      </Frame >
    )
  }

  if (stepIndex === 1 && TUTORIAL_ENABLED) {
    return (
      <Frame maxWidth="xs">
        <Box mx="auto" maxWidth={430}>
          <Heading>
            Optional:
            Watch a walk-through
            of the onboarding process.</Heading>
          <Box display={'flex'} justifyContent={'center'}>
            {/* embed here */}
            <video ref={videoRef} style={{ maxWidth: '50dvw' }} height='auto' controls >
              <source type="video/mp4" src={ONBOARD_WALKTHROUGH_URL} />
              Your browser does not support the video tag.
            </video>
          </Box>
        </Box>
        <Box mx="auto" my={3} maxWidth={280} display='flex' flexDirection={'column'}>
          <Box width={'100%'}>
            <Button variant="contained" type="submit" style={{ width: '100%', 'background-color': buttonColor }} onClick={() => videoRef.current.play()}>
              Watch Video
            </Button>
          </Box>
          <Box my={2} width={'100%'}>
            <Button variant="contained" type="submit" style={{ width: '100%', 'background-color': buttonColor }} onClick={() => setStepIndex(2)}>
              Begin Onboarding
            </Button>
          </Box>
        </Box>
      </Frame>
    )
  }

  return (
    <Frame maxWidth="xs">
      <Heading subheading={onboarding['contact-subheading'] || SUBSCRIPTIONS_ENABLED ? "It's always free for freelancers" : ''}>
        {AGE_GATE_ENABLED
          ? "Create your profile"
          : (onboarding['contact-heading'] || <>Welcome aboard!<br />Let's start with your basic contact info.</>)
        }
      </Heading>

      <form
        onSubmit={async (event) => {
          event.preventDefault()
          firebase.analytics().logEvent('user_freelancer_details_submit_click')
          await createUser({
            ...getFormValues(event),
            role: 'freelancer',
            displayPronouns,
            isOver18: AGE_GATE_ENABLED ? isOver18 : undefined
          })
        }}
      >
        {/* Avatar image handling */}
        {PROFILE_PICTURES_ENABLED && !ONBOARD_PROFILE_PICTURE_STEP_ENABLED && (
          <Box display={'flex'} justifyContent={'center'} my={1}>
            <input
              type="file"
              accept="image/jpeg, image/png"
              id="avatar"
              onChange={(e) => {
                const reader = new FileReader()
                // reader.onloadstart(() => {
                //   console.log("loading")
                // })
                reader.onload = () => {
                  // console.log(resizeAvatar(reader.result))
                  // setAvatarUrl(resizeAvatar(reader.result))
                }
                reader.onerror = (error) => {
                  console.log(error)
                }
                reader.readAsDataURL(e.target.files[0])
                // console.log(resizeAvatar(URL.createObjectURL(e.target.files[0])))
              }}
            // style={{ display: 'none'}}
            />
            <IconButton
              component="label"
              for="avatar"
              label="Upload Avatar">{avatarUrl ? <Avatar /> : <AddPhotoIcon />}</IconButton>
          </Box>
        )}
        {AGE_GATE_ENABLED && (
          <Box mt={2} mb={3} width="100%">
            <FormControlLabel
              control={
                <CustomSwitch
                  checked={isOver18}
                  onChange={(e) => setIsOver18(e.target.checked)}
                  name="isOver18"
                />
              }
              label="Are you over 18 years old?"
              labelPlacement="start"
              style={{
                margin: 0,
                width: '100%',
                display: 'flex',
                justifyContent: 'space-between'
              }}
            />
          </Box>
        )}

        <Box display="flex">
          <TextField required={!ONBOARD_SKIP_VALIDATION} name="firstName" label="First Name" />

          <Box mx={1} />

          <TextField required={!ONBOARD_SKIP_VALIDATION} name="lastName" label="Last Name" />
        </Box>

        {PRONOUNS_ENABLED && (
          <Box mt={2} display="flex" alignItems="center">
            <FormControlLabel
              control={
                <CustomSwitch
                  checked={displayPronouns}
                  onChange={(e) => setDisplayPronouns(e.target.checked)}
                  name="displayPronouns"
                />
              }
              label="Include pronouns?"
              labelPlacement="start"
            />
            <Box ml={2} style={{ width: '20%' }}>
              <TextField fullWidth name="pronouns" label="Pronouns" />
            </Box>
          </Box>
        )}

        <TextField
          required={!ONBOARD_SKIP_VALIDATION}
          name="website"
          label={AGE_GATE_ENABLED ? "Website (optional)" : "LinkedIn"}
          placeholder=""
        />
        <TextField required={!ONBOARD_SKIP_VALIDATION} name="email" label="Email Address" />
        <TextField required={!ONBOARD_SKIP_VALIDATION} name="password" type="password" label="Password" />
        <TextField
          name="phoneNumber"
          label="Phone Number"
          placeholder="Optional"
          value={phoneNumber}
          onChange={(e) => {
            const phoneNumber = formatPhoneNumber(e.target.value)
            setPhoneNumber(phoneNumber)
          }}
        />

        <Box mx="auto" maxWidth={280}>
          <Button type="submit" variant="contained" style={{ 'background-color': buttonColor }} my={2}>
            Next
          </Button>
        </Box>
      </form>

      <Box mx="auto" mb={3} maxWidth={280}>
        <Typography variant="body2">
          By signing up for an account, you agree to the{' '}
          <Typography component="a" href="/terms">
            Terms of service
          </Typography>
          .
        </Typography>
      </Box>

      <Divider />

      <Box mx="auto" my={3} maxWidth={280}>
        <Typography variant="body2">
          Already have an account?{' '}
          <Typography component="a" href="/login">
            Sign in
          </Typography>
        </Typography>
      </Box>
    </Frame>
  )
}

export default FreelancerCreateForm

const resizeAvatar = (img) => {
  const canvas = document.createElement('canvas')
  const context = canvas.getContext('2d')

  canvas.width = 128
  canvas.height = 128

  context.drawImage(img, 0, 0, 128, 128)

  return canvas.toDataURL()
}